// eslint-disable-next-line no-restricted-imports
import { DatePicker as AntdDatePicker } from 'antd'
import {
  addDays,
  addMonths,
  addYears,
  endOfMonth,
  formatDate,
  getDate,
  getDay,
  getHours,
  getMinutes,
  getMonth,
  getSeconds,
  getWeek,
  getYear,
  isAfter,
  isValid,
  setDate,
  setHours,
  setMilliseconds,
  setMinutes,
  setMonth,
  setSeconds,
  setYear,
  startOfWeek,
  parse as parseDate,
  format,
  type Day
} from 'date-fns'
import { ja } from 'date-fns/locale'

function localeParse(format: string) {
  return format
    .replace(/Y/g, 'y')
    .replace(/D/g, 'd')
    .replace(/gggg/, 'yyyy')
    .replace(/g/g, 'G')
    .replace(/([Ww])o/g, 'wo')
}

// 以下を参考にしつつ、バンドルサイズが肥大化しないようにロケールを ja のみに絞っている
// https://github.com/react-component/picker/blob/master/src/generate/dateFns.ts
export const DatePicker = AntdDatePicker.generatePicker<Date>({
  getNow: () => new Date(),
  getFixedDate: (string) => new Date(string),
  getEndDate: (date) => endOfMonth(date),
  getWeekDay: (date) => getDay(date),
  getYear: (date) => getYear(date),
  getMonth: (date) => getMonth(date),
  getDate: (date) => getDate(date),
  getHour: (date) => getHours(date),
  getMinute: (date) => getMinutes(date),
  getSecond: (date) => getSeconds(date),
  getMillisecond: (date) => date.getMilliseconds(),

  // set
  addYear: (date, diff) => addYears(date, diff),
  addMonth: (date, diff) => addMonths(date, diff),
  addDate: (date, diff) => addDays(date, diff),
  setYear: (date, year) => setYear(date, year),
  setMonth: (date, month) => setMonth(date, month),
  setDate: (date, num) => setDate(date, num),
  setHour: (date, hour) => setHours(date, hour),
  setMinute: (date, minute) => setMinutes(date, minute),
  setSecond: (date, second) => setSeconds(date, second),
  setMillisecond: (date, millisecond) => setMilliseconds(date, millisecond),

  // Compare
  isAfter: (date1, date2) => isAfter(date1, date2),
  isValidate: (date) => isValid(date),

  locale: {
    getWeekFirstDay: () => {
      return ja.options?.weekStartsOn ?? 0
    },
    getWeekFirstDate: (_locale, date) => {
      return startOfWeek(date, { locale: ja })
    },
    getWeek: (_locale, date) => {
      return getWeek(date, { locale: ja })
    },
    getShortWeekDays: () => {
      return Array.from({ length: 7 }).map((_, i) =>
        ja.localize.day(i as Day, { width: 'short' })
      )
    },
    getShortMonths: () => {
      return Array.from({ length: 12 }).map((_, i) =>
        ja.localize.month(i as Day, { width: 'abbreviated' })
      )
    },
    format: (_locale, date, format) => {
      if (!isValid(date)) {
        return ''
      }
      return formatDate(date, localeParse(format), {
        locale: ja
      })
    },
    parse: (_locale, text, formats) => {
      // 日付を入力欄で直接編集した場合、うまく入力されないので対応
      // https://github.com/ant-design/ant-design/issues/47241#issuecomment-1926357776
      const validFormat = formats.some((currentFormat) => {
        const dateFnsFormat = localeParse(currentFormat)
        const parsedDate = parseDate(text, dateFnsFormat, new Date(), {
          locale: ja
        })
        return isValid(parsedDate) && format(parsedDate, dateFnsFormat) === text
      })

      if (validFormat) {
        return parseDate(text, localeParse(formats[0]), new Date(), {
          locale: ja
        })
      } else {
        return null
      }
    }
  }
})
